/**
 * components/ui/FloatingActions.tsx
 * Floating action button on dashboard — New Note + Open Academy
 * Shows "NEW" badge on Academy button
 */
import React, { useState } from 'react';
import { Plus, X, GraduationCap, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../store';

const C = {
  amber300:'#F5A623', amber400:'#C4811A', ink:'#1A1814',
  white:'#FFFFFF', surface:'#FAFAF8', border:'#E8E4DC',
};
const font = { body:"'DM Sans',sans-serif", head:"'Sora',sans-serif" };

const FloatingActions: React.FC = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const createNote = useStore(s => s.createNote);

  const handleNewNote = async () => {
    setOpen(false);
    const note = await createNote(null);
    useStore.getState().setOpenNote(note.id);
  };

  const handleAcademy = () => {
    setOpen(false);
    navigate('/academy');
  };

  return (
    <>
      <style>{`
        @keyframes popIn{from{opacity:0;transform:scale(0.7) translateY(10px)}to{opacity:1;transform:scale(1) translateY(0)}}
        @keyframes pulse{0%,100%{box-shadow:0 0 0 0 rgba(245,166,35,0.4)}50%{box-shadow:0 0 0 8px rgba(245,166,35,0)}}
      `}</style>

      {/* Backdrop */}
      {open && (
        <div onClick={() => setOpen(false)}
          style={{ position:'fixed', inset:0, zIndex:9990, background:'rgba(0,0,0,0.3)', backdropFilter:'blur(2px)' }}/>
      )}

      <div style={{ position:'fixed', bottom:24, right:24, zIndex:9991, display:'flex', flexDirection:'column', alignItems:'flex-end', gap:12 }}>

        {/* Action buttons — show when open */}
        {open && (
          <>
            {/* Academy button */}
            <div style={{ display:'flex', alignItems:'center', gap:10, animation:'popIn .2s ease' }}>
              <span style={{ background:C.ink, color:C.white, fontSize:12, fontWeight:600, fontFamily:font.body, padding:'5px 10px', borderRadius:8, whiteSpace:'nowrap', boxShadow:'0 2px 8px rgba(0,0,0,0.2)' }}>
                Open Academy
              </span>
              <button onClick={handleAcademy}
                style={{ width:50, height:50, borderRadius:'50%', border:'none', background:`linear-gradient(135deg,#7C3AED,#6D28D9)`, color:C.white, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 4px 16px rgba(124,58,237,0.4)', position:'relative' }}>
                <GraduationCap size={22}/>
                {/* NEW badge */}
                <span style={{ position:'absolute', top:-4, right:-4, background:'#DC2626', color:C.white, fontSize:8, fontWeight:800, fontFamily:font.head, padding:'2px 4px', borderRadius:6, letterSpacing:'0.05em', lineHeight:1 }}>
                  NEW
                </span>
              </button>
            </div>

            {/* New note button */}
            <div style={{ display:'flex', alignItems:'center', gap:10, animation:'popIn .25s ease' }}>
              <span style={{ background:C.ink, color:C.white, fontSize:12, fontWeight:600, fontFamily:font.body, padding:'5px 10px', borderRadius:8, whiteSpace:'nowrap', boxShadow:'0 2px 8px rgba(0,0,0,0.2)' }}>
                New note
              </span>
              <button onClick={handleNewNote}
                style={{ width:50, height:50, borderRadius:'50%', border:'none', background:`linear-gradient(135deg,${C.amber300},${C.amber400})`, color:C.white, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:`0 4px 16px ${C.amber300}66` }}>
                <FileText size={22}/>
              </button>
            </div>
          </>
        )}

        {/* Main FAB button */}
        <button onClick={() => setOpen(s => !s)}
          style={{ width:58, height:58, borderRadius:'50%', border:'none', background:open?C.ink:`linear-gradient(135deg,${C.amber300},${C.amber400})`, color:C.white, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:open?'0 4px 20px rgba(0,0,0,0.3)':`0 4px 20px ${C.amber300}66`, transition:'all .2s ease', animation:!open?'pulse 2s infinite':undefined }}>
          {open ? <X size={24}/> : <Plus size={26}/>}
        </button>
      </div>
    </>
  );
};

export default FloatingActions;